import { useEffect, useState } from 'react';
import { Image } from 'react-native';

/**
 * Measures a remote image's natural aspect ratio (width / height) so a caller
 * can size the image's container to fit it instead of cropping it into a fixed
 * box. Returns `fallback` until the dimensions resolve (and if they fail / there
 * is no uri), so layout stays stable while the image loads.
 *
 * Works on web + native (`Image.getSize` is supported on both for remote URIs).
 */
export function useImageAspectRatio(uri: string | null | undefined, fallback: number): number {
  const [ratio, setRatio] = useState<number>(fallback);

  useEffect(() => {
    setRatio(fallback);
    if (!uri) return;

    let cancelled = false;
    Image.getSize(
      uri,
      (width, height) => {
        if (!cancelled && width > 0 && height > 0) setRatio(width / height);
      },
      () => {
        // Couldn't measure (network / decode error) — keep the fallback ratio.
      }
    );
    return () => {
      cancelled = true;
    };
  }, [uri, fallback]);

  return ratio;
}
